import { noop, throttle } from "@/libs/lodash"
import * as Devtools from "./devtools"
import type { MeasureOptions, PerformanceEntry, PerformanceSpan } from "./index.d"
import * as Sentry from "./sentry"

export type { MeasureOptions, PerformanceEntry, PerformanceSpan }

export const createPerf = (): PerformanceEntry =>
  createEntry({ devtools: Devtools, sentry: Sentry })

const noopEntry: PerformanceEntry = { mark: () => noopPerf }
const noopPerf: PerformanceSpan = { measure: noop, child: () => noopEntry }

/** Decorator for `PerformanceEntry`'s `mark` function. When throttled, `mark` call will be ignored, along with consequential `measure` and `child` calls. */
export const throttleMark = (
  entry: PerformanceEntry = noopEntry,
  wait: number,
): ((name: string) => PerformanceSpan) =>
  throttle((name: string) => entry.mark(name), wait, { throttledResult: () => noopPerf })

const createEntry = ({
  devtools,
  sentry,
}: {
  sentry: PerformanceEntry
  devtools: PerformanceEntry
}): PerformanceEntry => ({
  mark: createMark({ devtools, sentry }),
})

/** @returns `mark` function creating a new performance span */
const createMark =
  ({ devtools, sentry }: { sentry: PerformanceEntry; devtools: PerformanceEntry }) =>
  (name: string): PerformanceSpan => {
    const spans = { devtools: devtools.mark(name), sentry: sentry.mark(name) }

    return {
      measure: createMeasure(spans),
      child: createChild(spans),
    }
  }

/** @returns `measure` function completing the current performance span */
const createMeasure =
  ({ devtools, sentry }: { sentry: PerformanceSpan; devtools: PerformanceSpan }) =>
  (options?: MeasureOptions) => {
    const measurement = devtools.measure()
    sentry.measure(options)
    return measurement
  }

/** @returns function for creation `child` performance spans */
const createChild =
  ({ sentry, devtools }: { sentry: PerformanceSpan; devtools: PerformanceSpan }) =>
  () =>
    createEntry({
      devtools: devtools.child(),
      sentry: sentry.child(),
    })
